import {
  formattedNewWagerDetails,
  getNewWagersModalDetails
} from '@bo-utils/newWagers';
import type { FormattedNewWager } from '@library/types/newWagers';
import { PlayerLineupRow } from '@library/components/player/PlayerLineupRow';
import { cn } from '@library/component-utils/cn';
import { convertCentsToDollars } from '@library/utils';
import { useMemo } from 'react';

export interface MemberEntryModalContentProps {
  newWagerDetails: FormattedNewWager;
}

export const MemberEntryModalContent = ({
  newWagerDetails
}: MemberEntryModalContentProps) => {
  const { lineup, newWager } = newWagerDetails;
  const { formattedDescriptions, resultColor } = formattedNewWagerDetails(
    newWager,
    lineup
  );
  const wagerModalDetails = getNewWagersModalDetails(newWagerDetails);
  const specialReversionPayouts = newWager?.payoutData?.srp;

  // LINEUP
  const playerLineup = useMemo(() => {
    return lineup.map((lineupRow) => (
      <PlayerLineupRow key={lineupRow.id} lineupRow={lineupRow} isModalView />
    ));
  }, [lineup]);

  // PAYOUT MULTIPLIERS
  const payoutMultipliers = useMemo(() => {
    return Object.entries(
      newWager.payoutMultipliers || { no_result: null }
    ).map(([key, value], index) => {
      const amountToWin = convertCentsToDollars(
        newWager.amountBetCents * Number(value)
      );
      const amountWon = convertCentsToDollars(newWager.amountWonCents);
      const multiplierNumOfWins = `${lineup.length - index} of ${lineup.length}`;
      const multiplier = `${value}x`;
      const isFlex = newWager.lineupGameType.includes('Flex');

      if (key === 'no_result') {
        return (
          <span
            key={key}
            className="text-soClean-100 flex w-full justify-start text-sm"
          >
            N/A
          </span>
        );
      }

      return (
        <div
          key={key}
          className={cn(
            'bg-soFresh-120 flex flex-1 flex-col items-center justify-center rounded-lg border p-2 px-0',
            amountToWin === amountWon
              ? '!border-atlien-100'
              : 'border-soFresh-120'
          )}
        >
          <p className="text-atlien-100 text-sm">{amountToWin}</p>
          <p className="text-xs">
            {isFlex && (
              <>
                <span className="text-soClean-140">{multiplierNumOfWins}</span>
                <span className="text-soClean-100"> • </span>
              </>
            )}
            <span className="text-soClean-100">{multiplier}</span>
          </p>
        </div>
      );
    });
  }, [
    newWager.payoutMultipliers,
    newWager.amountBetCents,
    newWager.amountWonCents,
    lineup.length,
    newWager.lineupGameType
  ]);

  // WAGER DETAILS
  const wagerDetails = useMemo(() => {
    return wagerModalDetails.map(
      ({ containerClassName, key, label, value, valueClassName }) => {
        const isResult = key === 'result';
        const isPayoutMultipliers = key === 'payoutMultipliers';

        return (
          <div
            key={key}
            className={cn(
              'border-soFresh-140 flex-col items-start',
              containerClassName ?? ''
            )}
            data-testid={`wager-details-${key}`}
          >
            <p className="text-soClean-180 text-sm capitalize">{label}</p>

            {/* RESULT FORMATTING */}
            {isPayoutMultipliers ? (
              <div className="flex justify-evenly gap-x-2 pt-2">
                {payoutMultipliers}
              </div>
            ) : (
              <span
                className={cn(
                  'text-soClean-100 whitespace-pre-line text-right text-sm',
                  isResult ? `${resultColor} mt-0.5 uppercase` : 'capitalize',
                  valueClassName ?? ''
                )}
              >
                {value}
              </span>
            )}
          </div>
        );
      }
    );
  }, [wagerModalDetails, payoutMultipliers, resultColor]);

  // FORMATTED WAGER DESCRIPTIONS
  const wagerDescriptions = useMemo(() => {
    return formattedDescriptions.map((description) => (
      <span
        key={description}
        className="text-soClean-100 bg-soFresh-130 rounded-lg px-3 py-1 text-sm capitalize"
      >
        {description}
      </span>
    ));
  }, [formattedDescriptions]);

  // PAYOUT DATA SECTION
  const PayoutDataSection = useMemo(() => {
    return specialReversionPayouts?.map((payout, index) => {
      const numOfPlayersReverted = index + 1;
      let numOfCorrectPlays = lineup.length - numOfPlayersReverted;
      const payoutJoinString = payout.join('-');

      const payoutId = `payout_${payoutJoinString}`;
      const multiplierId = `multiplier_${payoutJoinString}`;

      return (
        <div
          key={payoutId}
          className="border-soFresh-140 border-b px-4 py-3 last:border-none"
        >
          {payout.map((multiplier, multiplierIndex) => {
            if (multiplier === 0) {
              return null;
            }
            if (multiplierIndex > 0) {
              numOfCorrectPlays -= 1; // Subtract 1 for each multiplier except the first
            }
            return (
              <div
                key={multiplierId}
                className="my-2 flex items-center justify-between"
              >
                <p className="text-soClean-180 flex gap-1 text-sm">
                  <span className="text-soClean-100">
                    {numOfPlayersReverted}
                  </span>
                  <span>players revert,</span>
                  <span className="text-soClean-100">{numOfCorrectPlays}</span>
                  <span>correct plays</span>
                </p>
                <div className="border-stankonia-100 bg-stankonia-100 h-auto w-11 rounded border text-center text-sm">
                  {multiplier}
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  }, [specialReversionPayouts, lineup.length]);

  return (
    <>
      {/* MODAL LEFT SECTION */}
      <div
        className="border-soFresh-140 no-scrollbar flex-1 overflow-scroll border-r pr-6"
        data-testid="member-entry-modal-player-lineup"
      >
        {/* PLAYER INFORMATION */}
        <p className="text-soClean-180 mb-1 text-sm">Player Information</p>
        <div className="border-soFresh-140 mb-6 rounded-lg border px-4 py-2">
          {playerLineup}
        </div>

        {/* DESCRIPTION */}
        <div
          className="flex flex-col"
          data-testid="member-entry-modal-wager-descriptions"
        >
          <p className="text-soClean-180 pb-2 text-sm">Description</p>
          <div className="flex flex-wrap gap-2">{wagerDescriptions}</div>
        </div>
      </div>

      {/* MODAL RIGHT SECTION */}
      <div className="no-scrollbar flex flex-1 flex-col overflow-scroll pl-6">
        {/* WAGER DETAILS */}
        <div
          className="flex flex-1 flex-wrap"
          data-testid="member-entry-modal-wager-details"
        >
          <div className="grid flex-1 grid-cols-2 gap-x-4">{wagerDetails}</div>
        </div>

        {/* PAYOUT DATA */}
        <p className="text-soClean-180 mb-1 text-sm">Payout Data</p>
        <div className="border-soFresh-140 rounded-lg border">
          {PayoutDataSection}
        </div>
      </div>
    </>
  );
};
